import * as Sentry from '@sentry/react';
import { LucideTriangleAlert, RefreshCw } from 'lucide-react';
import type { JSX } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';

import { Button } from '@/components/ui/button';

/**
 * Checks if the error is a chunk load error.
 *
 * @param error - The error to check.
 * @returns True if the error is a chunk load error, false otherwise.
 */
const isChunkLoadError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const err = error as Record<string, unknown>;

  const name = typeof err.name === 'string' ? err.name : '';
  const message = typeof err.message === 'string' ? err.message : '';

  return (
    name === 'ChunkLoadError' ||
    message.includes('Loading chunk') ||
    message.includes('dynamically imported module')
  );
};

/**
 * Gets the icon of the error.
 *
 * @param error - The error to get the icon of.
 * @returns The icon of the error.
 */
const getIcon = (error: unknown) => {
  return isChunkLoadError(error) ? (
    <RefreshCw
      className="mx-auto animate-spin dark:text-white"
      aria-hidden="true"
      size={32}
    />
  ) : (
    <LucideTriangleAlert
      className="mx-auto dark:text-white"
      aria-hidden="true"
      size={32}
    />
  );
};

/**
 * Gets the title of the error.
 *
 * @param error - The error to get the title of.
 * @returns The title of the error.
 */
const getTitle = (error: unknown) => {
  if (isChunkLoadError(error)) {
    return 'New version available!';
  }
  return isRouteErrorResponse(error) ? (
    <>
      {error.status} {error.statusText}
    </>
  ) : (
    'Something went wrong'
  );
};

/**
 * Gets the description of the error.
 *
 * @param error - The error to get the description of.
 * @returns The description of the error.
 */
const getDescription = (error: unknown) => {
  if (isChunkLoadError(error)) {
    return (
      <span>
        The application has been updated in the background. Please reload to see
        the latest changes.
      </span>
    );
  }
  return isRouteErrorResponse(error) ? (
    <span>{error.data || 'An error occurred when loading this page.'}</span>
  ) : (
    <span>
      {error instanceof Error ? error.message : 'An unknown error occurred.'}
    </span>
  );
};

/**
 * This is a fallback element for when an unhandled error occurs.
 *
 * @returns A fallback element for when the page is not loaded due to error.
 */
export default function ErrorBoundary(): JSX.Element {
  const error = useRouteError();

  return (
    <Sentry.ErrorBoundary
      fallback={
        <div className="grid h-screen w-screen place-content-center gap-2 dark:bg-zinc-900">
          {getIcon(error)}
          <h2 className="text-center text-xl dark:text-white">
            {getTitle(error)}
          </h2>
          <p className="text-muted-foreground max-w-sm text-center text-sm dark:text-zinc-400">
            {getDescription(error)}
          </p>
          <Button
            className="m-auto w-max"
            size="lg"
            onClick={() => window.location.reload()}
          >
            Reload Page
          </Button>
        </div>
      }
    />
  );
}
